import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch } from 'react-icons/fi';
import { geofenceService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Geofences = () => {
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch geofences data
  useEffect(() => {
    const fetchGeofences = async () => {
      try {
        setLoading(true);
        
        // In a real app, we would fetch from API
        // const response = await geofenceService.getGeofences({
        //   page: currentPage,
        //   limit: itemsPerPage,
        //   ...searchTerm ? { search: searchTerm } : {}
        // });
        
        // For now, use dummy data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const dummyGeofences = [
          {
            id: 'g1',
            name: 'Downtown Zone',
            description: 'Main commercial area coverage',
            type: 'circle',
            center: { lat: 40.7128, lng: -74.006 },
            radius: 1500, // in meters
            alertType: 'both',
            isActive: true,
            createdAt: '2023-06-15T10:30:00Z'
          },
          {
            id: 'g2',
            name: 'Airport Perimeter',
            description: 'Airport restricted access zone',
            type: 'polygon',
            vertices: [
              { lat: 40.6413, lng: -73.7781 },
              { lat: 40.6631, lng: -73.7675 },
              { lat: 40.6542, lng: -73.8101 },
              { lat: 40.6381, lng: -73.7948 }
            ],
            alertType: 'entry',
            isActive: true,
            createdAt: '2023-06-20T14:15:00Z'
          },
          {
            id: 'g3',
            name: 'Warehouse District',
            description: 'Main logistics hub',
            type: 'circle',
            center: { lat: 40.7234, lng: -74.0123 },
            radius: 800, // in meters
            alertType: 'both',
            isActive: true,
            createdAt: '2023-07-05T09:45:00Z'
          }
        ];

        setGeofences(dummyGeofences);
        setTotalPages(1); // In a real app, would be calculated from response
        setLoading(false);
      } catch (err) {
        setError('Failed to load geofences');
        setLoading(false);
        console.error('Error fetching geofences:', err);
      }
    };

    fetchGeofences();
  }, [currentPage, itemsPerPage, searchTerm]);

  // Filter geofences based on search term
  const filteredGeofences = geofences.filter(geofence => 
    geofence.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (geofence.description && geofence.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle delete geofence
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this geofence?')) {
      try {
        // In a real app, we would call API
        // await geofenceService.deleteGeofence(id);
        
        // For demo, just remove from local state
        setGeofences(prevGeofences => prevGeofences.filter(geofence => geofence.id !== id));
      } catch (err) {
        console.error('Error deleting geofence:', err);
      }
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">{error}</p>
        <button 
          className="mt-4 btn btn-primary" 
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Geofences</h1>
          <p className="text-gray-600">Manage your geographic boundaries</p>
        </div>
        <Link to="/geofences/new" className="btn btn-primary flex items-center">
          <FiPlus className="mr-2" />
          Create Geofence
        </Link>
      </div>

      {/* Search and filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 w-full"
              placeholder="Search geofences..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="flex">
            <select className="form-input">
              <option value="all">All Types</option>
              <option value="circle">Circle</option>
              <option value="polygon">Polygon</option>
            </select>
          </div>
        </div>
      </div>

      {/* Geofences list */}
      {filteredGeofences.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No geofences found. Create your first geofence to get started.</p>
          <Link to="/geofences/new" className="btn btn-primary mt-4 inline-flex items-center">
            <FiPlus className="mr-2" />
            Create Geofence
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alert Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGeofences.map(geofence => (
                <tr key={geofence.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{geofence.name}</div>
                    <div className="text-sm text-gray-500">{geofence.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {geofence.type.charAt(0).toUpperCase() + geofence.type.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {geofence.alertType.charAt(0).toUpperCase() + geofence.alertType.slice(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      geofence.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {geofence.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(geofence.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <Link 
                        to={`/geofences/${geofence.id}`}
                        className="text-primary hover:text-primary-dark"
                        title="View"
                      >
                        <FiEye />
                      </Link>
                      <Link 
                        to={`/geofences/${geofence.id}/edit`}
                        className="text-blue-600 hover:text-blue-900" 
                        title="Edit"
                      >
                        <FiEdit2 />
                      </Link>
                      <button 
                        onClick={() => handleDelete(geofence.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-primary hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-primary hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="inline-flex rounded-md shadow-sm">
                    {[...Array(totalPages).keys()].map(page => (
                      <button
                        key={page + 1}
                        onClick={() => handlePageChange(page + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          currentPage === page + 1
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        } ${
                          page === 0 ? 'rounded-l-md' : ''
                        } ${
                          page === totalPages - 1 ? 'rounded-r-md' : ''
                        }`}
                      >
                        {page + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Geofences;